import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getPresence, pingPresence, setPresenceAway } from '../services/presenceApi';
import { ADMIN_FLAG_KEY, useAuth } from './AuthContext';
import { PresenceContext, READ_EVERY_MS, shouldPing } from './PresenceContext';

/**
 * PresenceProvider (Support V1 phase 3): the app's presence ping and the one batched read (the rules and hooks are
 * PresenceContext.js). Mounted once, for the signed-in app.
 * Take 2 (review S3): only a person's own input counts: a key, a pointer press, the wheel, a touch. Never a scroll event
 * (a page scrolling itself to a new message fires one) and never a page load: lastInputAt starts at 0, nothing pings on
 * mount, and the input handler pings (throttled by shouldPing). Review N6: no ping before an admin tab knows whether it is
 * spoofing. Review N5: a failed read shows nobody online.
 */
const CHECK_EVERY_MS = 10 * 1000;
export const INPUT_EVENTS = ['keydown', 'pointerdown', 'wheel', 'touchstart'];
const EMPTY = new Set();

export const PresenceProvider = ({ children }) => {
  const { userProfile, isSpoofing, spoofingStatus, checkingSpoofing } = useAuth();
  const me = userProfile?.id ? String(userProfile.id) : '';
  const [online, setOnline] = useState(EMPTY);
  const [away, setAwayState] = useState(false);
  const watched = useRef(new Map());
  const lastInputAt = useRef(0);
  const lastPingAt = useRef(0);
  const soon = useRef(null);

  const visible = () => typeof document === 'undefined' || document.visibilityState === 'visible';

  const refresh = useCallback(() => {
    if (!me || !visible()) return;
    const ids = Array.from(new Set([me, ...watched.current.keys()])).slice(0, 200);
    getPresence(ids).then((res) => {
      setOnline(new Set((res?.data?.online || []).map(String)));
      setAwayState(!!res?.data?.away);
    }).catch(() => { setOnline(EMPTY); });  // the next tick retries; meanwhile nobody reads online
  }, [me]);

  const spoofKnown = useCallback(() => {
    let adminTab = false;
    try { adminTab = window.localStorage.getItem(ADMIN_FLAG_KEY) === 'true'; } catch (e) { adminTab = false; }
    return !adminTab || (spoofingStatus != null && !checkingSpoofing);
  }, [spoofingStatus, checkingSpoofing]);

  const tryPing = useCallback(() => {
    if (!me || isSpoofing || !spoofKnown()) return;
    const now = Date.now();
    if (!shouldPing({ visible: visible(), lastInputAt: lastInputAt.current, lastPingAt: lastPingAt.current, now })) return;
    lastPingAt.current = now;
    pingPresence().then(() => refresh(), () => { lastPingAt.current = 0; });
  }, [me, isSpoofing, spoofKnown, refresh]);

  useEffect(() => {
    if (!me) return undefined;
    const onInput = () => { lastInputAt.current = Date.now(); tryPing(); };
    const onVisible = () => { if (visible()) { tryPing(); refresh(); } };
    INPUT_EVENTS.forEach((e) => window.addEventListener(e, onInput, { passive: true, capture: true }));
    document.addEventListener('visibilitychange', onVisible);
    const pinger = setInterval(tryPing, CHECK_EVERY_MS);
    const reader = setInterval(refresh, READ_EVERY_MS);
    refresh();  // a read, never a ping: a page load is not input
    return () => {
      INPUT_EVENTS.forEach((e) => window.removeEventListener(e, onInput, { capture: true }));
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(pinger);
      clearInterval(reader);
      clearTimeout(soon.current);
    };
  }, [me, tryPing, refresh]);

  const watch = useCallback((ids) => {
    const added = [];
    (ids || []).forEach((id) => {
      const k = String(id || '');
      if (!k) return;
      const n = watched.current.get(k) || 0;
      if (!n) added.push(k);
      watched.current.set(k, n + 1);
    });
    if (added.length) {
      clearTimeout(soon.current);
      soon.current = setTimeout(refresh, 300);
    }
    return () => {
      (ids || []).forEach((id) => {
        const k = String(id || '');
        const n = watched.current.get(k) || 0;
        if (n <= 1) watched.current.delete(k); else watched.current.set(k, n - 1);
      });
    };
  }, [refresh]);

  const setAway = useCallback(async (value) => {
    setAwayState(!!value);
    try {
      await setPresenceAway(!!value);
    } catch (e) {
      setAwayState(!value);
      throw e;
    }
    refresh();
  }, [refresh]);

  const value = useMemo(() => ({
    me, online, away, setAway, watch, isOnline: (id) => online.has(String(id || '')),
  }), [me, online, away, setAway, watch]);
  return <PresenceContext.Provider value={value}>{children}</PresenceContext.Provider>;
};

